/**
 * Orchestration GraphQL Operations
 *
 * GraphQL queries and mutations for the Curiescious orchestration system.
 */

// ======== Mutations ========

export const executeOrchestrationMutation = {
  id: 'executeOrchestrationMutation' as const,
  op: 'executeOrchestration',
  query: `mutation executeOrchestration($input: ExecuteOrchestrationInput!) {
  executeOrchestration(input: $input) {
    planId
    status
    synthesis
    citations {
      id
      type
      identifier
      title
      authors
      journal
      year
      url
      text
      verified
    }
    keyFindings
    suggestedFollowUps
    metrics {
      totalDuration
      agentCount
      citationCount
      tokenUsage {
        inputTokens
        outputTokens
        totalTokens
        estimatedCost
      }
    }
    errors {
      agentType
      error
      recoverable
    }
    clarification {
      reason
      message
      suggestions
    }
  }
}`,
};

export const exportCitationsMutation = {
  id: 'exportCitationsMutation' as const,
  op: 'exportCitations',
  query: `mutation exportCitations($workspaceId: String!, $sessionId: String!, $format: CitationFormat!) {
  exportCitations(workspaceId: $workspaceId, sessionId: $sessionId, format: $format)
}`,
};

export const cancelOrchestrationMutation = {
  id: 'cancelOrchestrationMutation' as const,
  op: 'cancelOrchestration',
  query: `mutation cancelOrchestration($planId: String!) {
  cancelOrchestration(planId: $planId)
}`,
};

export const updateWorkspaceContextMutation = {
  id: 'updateWorkspaceContextMutation' as const,
  op: 'updateWorkspaceContext',
  query: `mutation updateWorkspaceContext($input: UpdateWorkspaceMemoryInput!) {
  updateWorkspaceContext(input: $input) {
    id
    workspaceId
    researchGoal
    researchFocus
    keyTerms
    importantEntities
    citationStyle
    responseStyle
    updatedAt
  }
}`,
};

export const createOrchestrationMemoryMutation = {
  id: 'createOrchestrationMemoryMutation' as const,
  op: 'createOrchestrationMemory',
  query: `mutation createOrchestrationMemory($input: CreateMemoryInput!) {
  createOrchestrationMemory(input: $input) {
    id
    workspaceId
    type
    scope
    content
    source
    confidence
    metadata
    createdAt
  }
}`,
};

export const deleteOrchestrationMemoryMutation = {
  id: 'deleteOrchestrationMemoryMutation' as const,
  op: 'deleteOrchestrationMemory',
  query: `mutation deleteOrchestrationMemory($workspaceId: String!, $memoryId: String!) {
  deleteOrchestrationMemory(workspaceId: $workspaceId, memoryId: $memoryId)
}`,
};

// ======== Queries ========

export const getOrchestrationPlanQuery = {
  id: 'getOrchestrationPlanQuery' as const,
  op: 'getOrchestrationPlan',
  query: `query getOrchestrationPlan($workspaceId: String!, $planId: String!) {
  currentUser {
    orchestration(workspaceId: $workspaceId) {
      plan(planId: $planId) {
        id
        sessionId
        query
        mode
        steps {
          id
          agentType
          task
          dependencies
          parallel
          timeout
          priority
          optional
        }
        estimatedTime
        requiredAgents
        status
        createdAt
        startedAt
        completedAt
      }
    }
  }
}`,
};

export const getOrchestrationHistoryQuery = {
  id: 'getOrchestrationHistoryQuery' as const,
  op: 'getOrchestrationHistory',
  query: `query getOrchestrationHistory($workspaceId: String!, $limit: Int, $offset: Int) {
  currentUser {
    orchestration(workspaceId: $workspaceId) {
      history(limit: $limit, offset: $offset) {
        id
        sessionId
        query
        mode
        status
        createdAt
        completedAt
      }
    }
  }
}`,
};

export const getAvailableAgentsQuery = {
  id: 'getAvailableAgentsQuery' as const,
  op: 'getAvailableAgents',
  query: `query getAvailableAgents($workspaceId: String!) {
  currentUser {
    orchestration(workspaceId: $workspaceId) {
      agents {
        name
        agentType
        description
        whenToUse
        tools
        model
        source
      }
    }
  }
}`,
};

export const getWorkspaceOrchestrationContextQuery = {
  id: 'getWorkspaceOrchestrationContextQuery' as const,
  op: 'getWorkspaceOrchestrationContext',
  query: `query getWorkspaceOrchestrationContext($workspaceId: String!) {
  currentUser {
    orchestration(workspaceId: $workspaceId) {
      workspaceContext {
        id
        workspaceId
        researchGoal
        researchFocus
        keyTerms
        importantEntities
        citationStyle
        responseStyle
        updatedAt
      }
    }
  }
}`,
};

export const searchOrchestrationMemoriesQuery = {
  id: 'searchOrchestrationMemoriesQuery' as const,
  op: 'searchOrchestrationMemories',
  query: `query searchOrchestrationMemories($workspaceId: String!, $type: MemoryType, $limit: Int) {
  currentUser {
    orchestration(workspaceId: $workspaceId) {
      memories(type: $type, limit: $limit) {
        id
        workspaceId
        type
        scope
        content
        source
        confidence
        metadata
        createdAt
        updatedAt
      }
    }
  }
}`,
};

// ======== Workspace Citations (Phase 7) ========

export const workspaceCitationsQuery = {
  id: 'workspaceCitationsQuery' as const,
  op: 'workspaceCitations',
  query: `query workspaceCitations($workspaceId: String!, $filter: WorkspaceCitationFilterInput) {
  currentUser {
    orchestration(workspaceId: $workspaceId) {
      citations(filter: $filter) {
        total
        items {
          id
          type
          identifier
          title
          authors
          journal
          year
          url
          abstract
          verified
          firstSeenAt
          lastSeenAt
          referenceCount
          referencedInDocs {
            docId
            docTitle
            blockId
            createdAt
          }
        }
      }
    }
  }
}`,
};

export const workspaceCitationReferencesQuery = {
  id: 'workspaceCitationReferencesQuery' as const,
  op: 'workspaceCitationReferences',
  query: `query workspaceCitationReferences($workspaceId: String!, $citationId: ID!) {
  currentUser {
    orchestration(workspaceId: $workspaceId) {
      citationReferences(citationId: $citationId) {
        docId
        docTitle
        blockId
        createdAt
      }
    }
  }
}`,
};

export const recordWorkspaceCitationsMutation = {
  id: 'recordWorkspaceCitationsMutation' as const,
  op: 'recordWorkspaceCitations',
  query: `mutation recordWorkspaceCitations($input: RecordWorkspaceCitationsInput!) {
  recordWorkspaceCitations(input: $input)
}`,
};

// ======== Workflow Studio ========

export const workspaceWorkflowTemplatesQuery = {
  id: 'workspaceWorkflowTemplatesQuery' as const,
  op: 'workspaceWorkflowTemplates',
  query: `query workspaceWorkflowTemplates($workspaceId: String!) {
  currentUser {
    orchestration(workspaceId: $workspaceId) {
      workflowTemplates {
        id
        workspaceId
        name
        description
        definition
        createdAt
        updatedAt
      }
    }
  }
}`,
};

export const saveRunAsWorkflowTemplateMutation = {
  id: 'saveRunAsWorkflowTemplateMutation' as const,
  op: 'saveRunAsWorkflowTemplate',
  query: `mutation saveRunAsWorkflowTemplate($input: SaveRunAsWorkflowTemplateInput!) {
  saveRunAsWorkflowTemplate(input: $input) {
    id
    name
    definition
    createdAt
  }
}`,
};

export const createWorkflowTemplateMutation = {
  id: 'createWorkflowTemplateMutation' as const,
  op: 'createWorkflowTemplate',
  query: `mutation createWorkflowTemplate($input: SaveWorkflowTemplateInput!) {
  createWorkflowTemplate(input: $input) {
    id
    name
    definition
    createdAt
  }
}`,
};

export const deleteWorkflowTemplateMutation = {
  id: 'deleteWorkflowTemplateMutation' as const,
  op: 'deleteWorkflowTemplate',
  query: `mutation deleteWorkflowTemplate($input: DeleteWorkflowTemplateInput!) {
  deleteWorkflowTemplate(input: $input)
}`,
};

export const runWorkflowMutation = {
  id: 'runWorkflowMutation' as const,
  op: 'runWorkflow',
  query: `mutation runWorkflow($input: RunWorkflowInput!) {
  runWorkflow(input: $input) {
    status
    finalSynthesis
    citationCount
    steps
  }
}`,
};
